/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { addMinutes, endOfDay, format, isToday } from 'date-fns'
import { Link } from 'react-router-dom'
import { Field, Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import { useQuery } from '@apollo/client'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { DateTimeInputField } from 'components/DateTimeInput'
import {
  validateRequired,
  validateRequiredNumber,
  validateRequiredPercentage,
  validateRequiredPromoCode,
} from 'utils/validation'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import {
  CreatePromocodeInput,
  OrderDirection,
  ProductTypeEnum,
  PromocodeStatusEnum,
  PromocodeTypeEnum,
} from '__generatedTypes__/globalTypes'
import { findAllAdditionalTests, findAllAdditionalTestsVariables } from '__generatedTypes__/findAllAdditionalTests'
import { findOnePromoCode_findOnePromocode as PromoCode } from '__generatedTypes__/findOnePromoCode'
import { PriceInput } from 'components/Input/PriceInput'
import { convertToCents, convertToDollars } from 'utils/priceFormatter'
import { FIND_ALL_PRODUCT } from 'queries/products'
import { findAllProduct, findAllProductVariables } from '__generatedTypes__/findAllProduct'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { FIND_ALL_ADDITIONAL_TESTS } from 'queries/additionalTests'
import { getTimeZoneAgnosticDate } from 'utils/getTimeZoneAgnosticDate'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import * as commonClasses from '../../Video/commonStyles'
import * as classes from './styles'
import { canDisablePauseToken, getProductOptionLabel } from './utils'

export const convertToSavedPercentage = (percentageDecimal: number) => Math.round(percentageDecimal * 100)
export const convertFromSavedPercentage = (percentageInteger: number) =>
  parseFloat((percentageInteger * 0.01).toFixed(2))

enum ProductType {
  AdditionalTest = 'AdditionalTest',
  Product = 'Product',
}

export type Props = {
  platform: Platform
  promoCode?: PromoCode
  onSubmit: (promoCode: CreatePromocodeInput) => Promise<void>
  onDelete?: () => Promise<void>
}

const PROMO_CODE_STATUSES: Array<{ id: PromocodeStatusEnum; title: string }> = [
  {
    id: PromocodeStatusEnum.Active,
    title: 'Active',
  },
  {
    id: PromocodeStatusEnum.Expired,
    title: 'Expired',
  },
]
const PROMO_CODE_TYPES: Array<{ id: PromocodeTypeEnum; title: string }> = [
  {
    id: PromocodeTypeEnum.Fixed,
    title: 'Fixed',
  },
  {
    id: PromocodeTypeEnum.Percentage,
    title: 'Percentage',
  },
]

type FormValues = Omit<PromoCode, 'type' | 'status' | 'additionalTest' | 'product'> & {
  type?: { id: PromocodeTypeEnum; title: string }
  status?: { id: PromocodeStatusEnum; title: string }
  additionalTest: string[]
  product: string[]
}

const validateProducts = (allowedIds: string[]) => (value?: string[], allValues?: Partial<FormValues>) => {
  const selectedIds = [...(value ?? []), ...(allValues?.additionalTest ?? [])]
  if (!allowedIds.some((id) => selectedIds.includes(id))) {
    return 'Please select at least one Membership Type'
  }

  return undefined
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const prepareForSubmit = (values: Partial<FormValues>): CreatePromocodeInput => ({
  amount:
    values.type?.id === PromocodeTypeEnum.Fixed
      ? convertToCents(Number(values.amount ?? 0))
      : convertToSavedPercentage(Number(values.amount ?? 0)),
  changeMembershipLengthBy: values.changeMembershipLengthBy ?? null,
  code: values.code,
  description: values.description ?? '',
  expirationDate:
    values.status?.id === PromocodeStatusEnum.Expired
      ? format(addMinutes(new Date(), 1), 'yyyy-MM-dd')
      : values.expirationDate
      ? format(new Date(values.expirationDate), 'yyyy-MM-dd')
      : null,
  maxUses: values.maxUses || undefined,
  product: values.product?.map((id) => ({ id })) ?? [],
  additionalTest: values.additionalTest?.map((id) => ({ id })) ?? [],
  name: values.name,
  status: values.status?.id ?? PromocodeStatusEnum.Active,
  type: values.type?.id ?? PromocodeTypeEnum.Fixed,
  isPauseTokenDisabled: values.isPauseTokenDisabled ?? false,
})

export const getInitialValues = (promoCode?: PromoCode): Partial<FormValues> => ({
  ...promoCode,
  changeMembershipLengthBy: promoCode?.changeMembershipLengthBy ?? undefined,
  amount: promoCode?.amount
    ? promoCode.type === PromocodeTypeEnum.Fixed
      ? convertToDollars(promoCode.amount)
      : convertFromSavedPercentage(promoCode.amount)
    : undefined,
  expirationDate: promoCode?.expirationDate
    ? endOfDay(new Date(getTimeZoneAgnosticDate(promoCode.expirationDate)))
    : undefined,
  additionalTest: promoCode?.additionalTest?.map(({ id }) => id) ?? [],
  product: promoCode?.product?.map(({ id }) => id) ?? [],
  isPauseTokenDisabled: promoCode?.isPauseTokenDisabled ?? false,
  status: PROMO_CODE_STATUSES.find(({ id }) => id === (promoCode?.status ?? PromocodeStatusEnum.Active)),
  type: PROMO_CODE_TYPES.find(({ id }) => id === (promoCode?.type ?? PromocodeTypeEnum.Fixed)),
})

export const Form = ({ platform, promoCode, onSubmit, onDelete }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [initialValues, setInitialValues] = useState(getInitialValues(promoCode))
  const { data: findAllProductData, loading: isLoadingProducts } = useQuery<findAllProduct, findAllProductVariables>(
    FIND_ALL_PRODUCT,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          order: {
            price: OrderDirection.ASC,
          },
          where: {
            platform: {
              id: platform.id,
            },
          },
        },
      },
    }
  )

  const { data: findAllAdditionalTestsData, loading: isLoadingAdditionalTests } = useQuery<
    findAllAdditionalTests,
    findAllAdditionalTestsVariables
  >(FIND_ALL_ADDITIONAL_TESTS, {
    fetchPolicy: 'network-only',
    variables: {
      query: {
        where: {
          subject: {
            platform: {
              id: platform.id,
            },
          },
        },
      },
    },
  })

  useEffect(() => {
    setInitialValues(getInitialValues(promoCode))
  }, [promoCode?.id])

  // Check if any of the selected product IDs is a regular membership or extension product
  const hasRegularMembershipOrExtensionProduct = useCallback(
    (selectedProductIds: string[]) => {
      const products = findAllProductData?.findAllProduct?.items || []
      return products.some(
        (p) =>
          selectedProductIds.includes(p.id) &&
          [ProductTypeEnum.Premium, ProductTypeEnum.PremiumMax, ProductTypeEnum.Extension].includes(p.type) &&
          !p.isSubscription
      )
    },
    [findAllProductData]
  )

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const selectedProductIds = [...values.product, ...values.additionalTest]
        const isPauseTokenDisableAvailable = canDisablePauseToken(
          platform.slug,
          values.product,
          findAllProductData?.findAllProduct?.items
        )

        const valuesToSubmit = {
          ...values,
          changeMembershipLengthBy: hasRegularMembershipOrExtensionProduct(selectedProductIds)
            ? values.changeMembershipLengthBy
            : null,
          isPauseTokenDisabled: isPauseTokenDisableAvailable ? values.isPauseTokenDisabled : false,
        }

        const promoCodeData = prepareForSubmit(valuesToSubmit)
        await onSubmit(promoCodeData)
      } catch (e) {
        const message = (e as Error).message
        toast.error('Failed to prepare data for submit' + message ? `, ${message}` : '')
      }
    },
    [onSubmit, hasRegularMembershipOrExtensionProduct, findAllProductData, platform.slug]
  )

  const handleDelete = useCallback(async () => {
    setSubmitting(true)
    try {
      await onDelete?.()
    } catch (e) {
      console.error(e)
    }
    setSubmitting(false)
  }, [onDelete])

  const membershipTypes = useMemo(
    () => [
      ...((findAllProductData?.findAllProduct?.items?.filter(({ type }) => type !== ProductTypeEnum.Free) || []).map(
        ({ id, name, duration, type, isSubscription }) => ({
          name: getProductOptionLabel({ name, duration, type }),
          id,
          type: ProductType.Product,
          isSubscription,
        })
      ) || []),
      ...(findAllAdditionalTestsData?.findAllAdditionalTest?.items || []).map((item) => ({
        name: item.name,
        id: item.id,
        type: ProductType.AdditionalTest,
        isSubscription: false,
      })),
    ],
    [findAllProductData, findAllAdditionalTestsData]
  )

  if (isLoadingProducts || isLoadingAdditionalTests) {
    return <PageLoader />
  }

  return (
    <div css={commonClasses.container}>
      <Typography variant="title3" css={commonClasses.title}>
        {initialValues.id ? 'Edit Discount Code' : 'Create Discount Code'}
      </Typography>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, initialValues, submitting: formSubmitting, form, values }) => (
          <form onSubmit={handleSubmit} css={commonClasses.contentContainer}>
            <div css={commonClasses.block}>
              <InputField name="name" validate={validateRequired} label="Name" placeholder="Enter Name" fullWidth />
              <InputField
                name="description"
                validate={validateRequired}
                label="Description"
                placeholder="Enter Description"
                fullWidth
                multiline
              />
              <InputField
                name="code"
                validate={validateRequiredPromoCode}
                label="Code"
                placeholder="Enter Code"
                fullWidth
              />
              <SelectField
                name="type"
                validate={validateRequired}
                items={PROMO_CODE_TYPES}
                label="Discount Type"
                placeholder="Choose Discount Type"
                onChange={() => form.change('amount', undefined)}
                displaySelectedItemsFirst
                fullWidth
              />
              <PriceInput
                name="amount"
                validate={
                  values.type?.id === PromocodeTypeEnum.Percentage ? validateRequiredPercentage : validateRequiredNumber
                }
                label="Amount"
                prefix={values.type?.id === PromocodeTypeEnum.Fixed ? '$' : undefined}
                suffix={values.type?.id === PromocodeTypeEnum.Percentage ? '%' : undefined}
                placeholder="Enter Amount"
                fullWidth
              />
              {/* Make field name as product but inside this field manually set values both for product and additionalTest */}
              <Field name="product" validate={validateProducts(membershipTypes.map(({ id }) => id))}>
                {({ meta }) => (
                  <div>
                    <div css={classes.labelWrapper}>Membership Type</div>
                    <div css={classes.optionsWrapper}>
                      {membershipTypes.length ? (
                        membershipTypes.map(({ name, id: value, type }) => {
                          const isSelected = [...values.product, ...values.additionalTest].includes(value)
                          return (
                            <FormControlLabel
                              key={value}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onClick={() => {
                                    const fieldName = type === ProductType.Product ? 'product' : 'additionalTest'
                                    const nextSelectedValues = isSelected
                                      ? values[fieldName].filter((val) => val !== value)
                                      : [...values[fieldName], value]

                                    form.change(fieldName, nextSelectedValues)

                                    if (
                                      fieldName === 'product' &&
                                      !canDisablePauseToken(
                                        platform.slug,
                                        nextSelectedValues,
                                        findAllProductData?.findAllProduct?.items
                                      )
                                    ) {
                                      form.change('isPauseTokenDisabled', false)
                                    }
                                  }}
                                />
                              }
                              label={name}
                            />
                          )
                        })
                      ) : (
                        <FormHelperText error>
                          No products were found for this platform. Please create at least one non-free product
                        </FormHelperText>
                      )}
                    </div>
                    {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
                  </div>
                )}
              </Field>
              {canDisablePauseToken(platform.slug, values.product, findAllProductData?.findAllProduct?.items) && (
                <div>
                  <div css={classes.labelWrapper}>Pause Token Configuration</div>
                  <div css={classes.optionsWrapper}>
                    <Field name="isPauseTokenDisabled" type="checkbox">
                      {({ input }) => (
                        <FormControlLabel label="Disable Pause Tokens" control={<Checkbox {...input} />} />
                      )}
                    </Field>
                  </div>
                </div>
              )}
              <DateTimeInputField
                name="expirationDate"
                label="Expiration Date"
                placeholder="Enter Expiration Date"
                pickerType="date"
                format="d MMMM, yyyy"
                disabled={values.status?.id === PromocodeStatusEnum.Expired}
                shouldDisableDate={isToday}
                disablePast
                withClearIcon
                fullWidth
              />
              <SelectField
                name="status"
                validate={validateRequired}
                items={PROMO_CODE_STATUSES}
                label="Status"
                placeholder="Choose Status"
                onChange={(status) => {
                  if (status?.id === PromocodeStatusEnum.Expired) {
                    form.change('expirationDate', new Date())
                  } else if (values.expirationDate && isToday(values.expirationDate)) {
                    form.change('expirationDate', undefined)
                  }
                }}
                displaySelectedItemsFirst
                fullWidth
              />
              {!!promoCode?.id && (
                <PriceInput
                  name="alreadyUsed"
                  label="Already Used"
                  placeholder="Enter Already Used"
                  decimalScale={0}
                  fullWidth
                  disabled
                />
              )}
              <PriceInput
                name="maxUses"
                validate={(value?: string) => {
                  if (value == undefined || value === '') {
                    return 'Required'
                  }
                  if (+value <= (values.alreadyUsed ?? 0)) {
                    return 'Must be greater then ' + (values.alreadyUsed ? `Already Used (${values.alreadyUsed})` : 0)
                  }
                  return undefined
                }}
                label="Max Uses"
                placeholder="Enter Max Uses"
                decimalScale={0}
                fullWidth
              />
              {hasRegularMembershipOrExtensionProduct([...values.product, ...values.additionalTest]) && (
                <PriceInput
                  name="changeMembershipLengthBy"
                  label="Change Membership length by (affects only regular memberships and extensions)"
                  placeholder="Enter Change Membership length by"
                  decimalScale={0}
                  allowNegative
                  fullWidth
                />
              )}
              <div css={classes.buttonsWrapper}>
                <div>
                  {!!initialValues.id && (
                    <Button
                      variant="outlined"
                      color="red"
                      onClick={handleDelete}
                      disabled={formSubmitting || submitting}
                      size="large"
                    >
                      <TrashIcon />
                      Delete
                    </Button>
                  )}
                </div>
                <div>
                  {/* eslint-disable-next-line react/display-name, @typescript-eslint/no-explicit-any */}
                  <Link to={-1 as any}>
                    <Button variant="outlined" color="blue" disabled={formSubmitting || submitting} size="large">
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="submit"
                    variant="contained"
                    color="blue"
                    disabled={formSubmitting || submitting}
                    size="large"
                  >
                    {initialValues.id ? 'Confirm Edits' : 'Create'}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      />
    </div>
  )
}
